import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Building, Trash2, RefreshCw, MapPin, Eye, Edit } from 'lucide-react';

const BuildingIntelligenceManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/building-intelligence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to load building intelligence reports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (reportId) => {
    setReportToDelete(reportId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!reportToDelete) return;
    
    try {
      console.log(`Deleting report ID: ${reportToDelete}`);
      await axios.delete(`${API}/building-intelligence/${reportToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Building intelligence report deleted successfully');
      setShowDeleteDialog(false);
      setReportToDelete(null);
      fetchReports();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete report');
      setShowDeleteDialog(false);
      setReportToDelete(null);
    }
  };

  const viewReport = (report) => {
    setSelectedReport(report);
    setShowDetailModal(true);
  };

  const editReport = (report) => {
    // Navigate to edit form with report data
    navigate('/field/building-intelligence', { state: { editData: report } });
  };

  const getPropertyTypeColor = (type) => {
    const colors = {
      'bank': 'bg-blue-100 text-blue-800',
      'office': 'bg-purple-100 text-purple-800',
      'school': 'bg-green-100 text-green-800',
      'retail': 'bg-orange-100 text-orange-800',
      'mixed_use': 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Building className="h-6 w-6 text-purple-600" />
          <h2 className="text-2xl font-bold">Building Intelligence Management</h2>
        </div>
        <Button onClick={fetchReports} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="mb-4">
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-5 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{reports.length}</p>
                <p className="text-sm text-gray-500">Total Reports</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{reports.filter(r => r.property_type === 'bank').length}</p>
                <p className="text-sm text-gray-500">Banks</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{reports.filter(r => r.property_type === 'office').length}</p>
                <p className="text-sm text-gray-500">Offices</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{reports.filter(r => r.property_type === 'school').length}</p>
                <p className="text-sm text-gray-500">Schools</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{reports.filter(r => r.property_type === 'retail').length}</p>
                <p className="text-sm text-gray-500">Retail</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">No building intelligence reports found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <Card key={report.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{report.property_name}</CardTitle>
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                      <MapPin className="h-4 w-4" />
                      <span>{report.address}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span><strong>Entry Date:</strong> {_fmtDate(report.date_of_entry)}</span>
                      <span>•</span>
                      <span><strong>Inspector:</strong> {report.inspector_name}</span>
                    </div>
                  </div>
                  <Badge className={getPropertyTypeColor(report.property_type)}>
                    {report.property_type.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {report.property_profile?.management_company && (
                    <div>
                      <p className="text-sm text-gray-700"><strong>Management:</strong></p>
                      <p className="text-sm text-gray-600">{report.property_profile.management_company}</p>
                    </div>
                  )}
                  {report.property_profile?.num_floors && (
                    <div>
                      <p className="text-sm text-gray-700"><strong>Floors:</strong></p>
                      <p className="text-sm text-gray-600">{report.property_profile.num_floors}</p>
                    </div>
                  )}
                  {report.property_profile?.num_tenants && (
                    <div>
                      <p className="text-sm text-gray-700"><strong>Tenants:</strong></p>
                      <p className="text-sm text-gray-600">{report.property_profile.num_tenants}</p>
                    </div>
                  )}
                  {report.access_control?.access_method && (
                    <div>
                      <p className="text-sm text-gray-700"><strong>Access Method:</strong></p>
                      <p className="text-sm text-gray-600">{report.access_control.access_method}</p>
                    </div>
                  )}
                </div>

                {report.special_instructions && (
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <p className="text-sm text-gray-700 mb-1"><strong>Special Instructions:</strong></p>
                    <p className="text-sm text-gray-600">{report.special_instructions}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-xs text-gray-500">
                    <span>Submitted: {_fmtDateTime(report.submitted_at)}</span>
                    {report.photos && report.photos.length > 0 && (
                      <span className="ml-4">📸 {report.photos.length} photos</span>
                    )}
                    {report.interior_intelligence && report.interior_intelligence.length > 0 && (
                      <span className="ml-4">🏢 {report.interior_intelligence.length} floor reports</span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewReport(report);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        editReport(report);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteReport(report.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Building Intelligence Report Details</DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-600">Property Name</p>
                  <p className="text-base">{selectedReport.property_name}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Property Type</p>
                  <p className="text-base capitalize">{selectedReport.property_type?.replace('_', ' ')}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-semibold text-gray-600">Address</p>
                  <p className="text-base">{selectedReport.address}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Entry Date</p>
                  <p className="text-base">{_fmtDate(selectedReport.date_of_entry)}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Inspector</p>
                  <p className="text-base">{selectedReport.inspector_name}</p>
                </div>
              </div>

              {selectedReport.property_profile && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Property Profile</p>
                  <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded">
                    {selectedReport.property_profile.management_company && (
                      <div>
                        <p className="text-xs text-gray-500">Management Company</p>
                        <p className="text-sm">{selectedReport.property_profile.management_company}</p>
                      </div>
                    )}
                    {selectedReport.property_profile.num_floors && (
                      <div>
                        <p className="text-xs text-gray-500">Number of Floors</p>
                        <p className="text-sm">{selectedReport.property_profile.num_floors}</p>
                      </div>
                    )}
                    {selectedReport.property_profile.num_tenants && (
                      <div>
                        <p className="text-xs text-gray-500">Number of Tenants</p>
                        <p className="text-sm">{selectedReport.property_profile.num_tenants}</p>
                      </div>
                    )}
                    {selectedReport.property_profile.building_sq_ft && (
                      <div>
                        <p className="text-xs text-gray-500">Building Sq Ft</p>
                        <p className="text-sm">{selectedReport.property_profile.building_sq_ft}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedReport.access_control && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Access Control</p>
                  <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded">
                    {selectedReport.access_control.access_method && (
                      <div>
                        <p className="text-xs text-gray-500">Access Method</p>
                        <p className="text-sm">{selectedReport.access_control.access_method}</p>
                      </div>
                    )}
                    {selectedReport.access_control.security_requirements && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">Security Requirements</p>
                        <p className="text-sm">{selectedReport.access_control.security_requirements}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {selectedReport.special_instructions && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">Special Instructions</p>
                  <p className="text-base p-3 bg-gray-50 rounded">{selectedReport.special_instructions}</p>
                </div>
              )}

              {selectedReport.interior_intelligence && selectedReport.interior_intelligence.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Interior Intelligence ({selectedReport.interior_intelligence.length} floors)</p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {selectedReport.interior_intelligence.map((floor, idx) => (
                      <div key={idx} className="p-3 border rounded">
                        <p className="font-medium">Floor {floor.floor_number}</p>
                        {floor.restrooms && <p className="text-sm">Restrooms: {floor.restrooms}</p>}
                        {floor.notes && <p className="text-sm text-gray-600">{floor.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedReport.photos && selectedReport.photos.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-600 mb-2">Photos ({selectedReport.photos.length})</p>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedReport.photos.map((photo, idx) => (
                      <img key={idx} src={photo} alt={`Photo ${idx + 1}`} className="w-full h-32 object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}

              <div className="text-sm text-gray-500 pt-4 border-t">
                Submitted: {_fmtDateTime(selectedReport.submitted_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this building intelligence report? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default BuildingIntelligenceManagement;
